import React, { useEffect, useState } from 'react';
import { Users, Eye, Search, Download, TrendingUp, DollarSign, ChevronRight } from 'lucide-react';
import API from '../../api';

export default function AdminUserTransactions() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userParties, setUserParties] = useState([]);
  const [showPartiesModal, setShowPartiesModal] = useState(false);
  const [loadingParties, setLoadingParties] = useState(false);
  const [error, setError] = useState('');
  
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyTransactions, setPartyTransactions] = useState([]);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await API.get('/users/admin/users-stats');
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setError(error.response?.data?.message || 'Failed to load users');
      setLoading(false);
    }
  };

  const viewUserParties = async (user) => {
    setSelectedUser(user);
    setShowPartiesModal(true);
    setLoadingParties(true);
    
    try {
      const response = await API.get(`/users/admin/user-parties/${user._id}`);
      setUserParties(response.data);
      setLoadingParties(false);
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.message || 'Failed to load parties');
      setLoadingParties(false);
    }
  };

  const viewPartyTransactions = async (party) => {
    setSelectedParty(party);
    setShowTransactionsModal(true);
    setLoadingTransactions(true);
    
    try {
      const response = await API.get(`/transactions/admin/user/${selectedUser._id}`);
      const filtered = response.data.filter(t => t.party && t.party._id === party._id);
      setPartyTransactions(filtered);
      setLoadingTransactions(false);
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.message || 'Failed to load transactions');
      setLoadingTransactions(false);
    }
  };

  const closeTransactionsModal = () => {
    setShowTransactionsModal(false);
    setSelectedParty(null);
    setPartyTransactions([]);
  };

  const closePartiesModal = () => {
    setShowPartiesModal(false);
    setSelectedUser(null);
    setUserParties([]);
  };

  const exportToCSV = () => {
    if (!partyTransactions.length) return;
    
    const headers = ['Date', 'Party', 'Type', 'Invoice', 'Amount'];
    const rows = partyTransactions.map(t => [
      new Date(t.date).toLocaleDateString(),
      t.party?.name || '-',
      t.type,
      t.invoiceNumber || '-',
      t.amount
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedParty.name}_transactions.csv`;
    a.click();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NP', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'purchase': return '#667eea';
      case 'payment': return '#48bb78';
      case 'return': return '#ed8936';
      case 'sale': return '#38b2ac';
      default: return '#718096';
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f7fa' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }}></div>
          <p style={{ margin: 0, fontSize: '1.2rem', color: '#4a5568' }}>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f7fa' }}>
        <div style={{ textAlign: 'center', maxWidth: 500, padding: 40, background: 'white', borderRadius: 16, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <svg style={{ width: 56, height: 56, color: '#e53e3e', margin: '0 auto 20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 style={{ fontSize: 22, fontWeight: 700, color: '#2d3748', marginBottom: 12 }}>Error Loading Users</h3>
          <p style={{ color: '#718096', marginBottom: 24, fontSize: 15 }}>{error}</p>
          <button 
            onClick={fetchUsers}
            style={{ padding: '12px 32px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '30px', background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '2rem', fontWeight: 700, color: '#1a202c' }}>User Transactions</h1>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '1rem' }}>View and manage all user transactions by party</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #3b82f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '40px', height: '40px', background: '#eff6ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={20} color="#3b82f6" />
            </div>
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280', fontWeight: 500 }}>Total Users</h3>
          </div>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#1a202c' }}>{users.length}</p>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #10b981' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '40px', height: '40px', background: '#d1fae5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={20} color="#10b981" />
            </div>
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280', fontWeight: 500 }}>Total Purchases</h3>
          </div>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#1a202c' }}>
            {formatCurrency(users.reduce((sum, u) => sum + (u.totalPurchases || 0), 0))}
          </p>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '4px solid #f59e0b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '40px', height: '40px', background: '#fef3c7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={20} color="#f59e0b" />
            </div>
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280', fontWeight: 500 }}>Total Payable</h3>
          </div>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#1a202c' }}>
            {formatCurrency(users.reduce((sum, u) => sum + (u.payable || 0), 0))}
          </p>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ position: 'relative' }}>
          <Search size={20} color="#9ca3af" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '14px 14px 14px 48px', borderRadius: '10px', border: '2px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>User</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Transactions</th>
                <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Purchases</th>
                <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Payments</th>
                <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Payable</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr key={user._id} style={{ borderBottom: '1px solid #f3f4f6', background: index % 2 === 0 ? 'white' : '#fafbfc' }}>
                  <td style={{ padding: '16px' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1a202c', fontSize: '0.95rem' }}>{user.name}</div>
                      <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '2px' }}>{user.email}</div>
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{ padding: '6px 12px', borderRadius: '20px', background: '#eff6ff', color: '#1e40af', fontSize: '0.85rem', fontWeight: 600 }}>
                      {user.totalTransactions || 0}
                    </span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: '#667eea' }}>{formatCurrency(user.totalPurchases)}</td>
                  <td style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: '#48bb78' }}>{formatCurrency(user.totalPayments)}</td>
                  <td style={{ padding: '16px', textAlign: 'right' }}>
                    <span style={{ fontWeight: 700, color: user.payable > 0 ? '#dc2626' : '#16a34a' }}>{formatCurrency(user.payable)}</span>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button
                      onClick={() => viewUserParties(user)}
                      style={{ padding: '8px 16px', borderRadius: '8px', background: '#3b82f6', border: 'none', color: 'white', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}
                      onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                    >
                      <Eye size={16} />View Parties
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredUsers.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#6b7280' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔍</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', fontWeight: 600 }}>No Users Found</h3>
          </div>
        )}
      </div>

      {showPartiesModal && selectedUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '20px' }} onClick={closePartiesModal}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: 700, color: '#1a202c' }}>{selectedUser.name}'s Parties</h2>
              <p style={{ margin: 0, color: '#6b7280' }}>Click on any party to view their transactions</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #3b82f6' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Total Purchases</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a202c' }}>{formatCurrency(selectedUser.totalPurchases)}</div>
              </div>
              <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #10b981' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Total Payments</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a202c' }}>{formatCurrency(selectedUser.totalPayments)}</div>
              </div>
              <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #ef4444' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Amount Payable</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a202c' }}>{formatCurrency(selectedUser.payable)}</div>
              </div>
            </div>

            {loadingParties ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }}></div>
                <p style={{ margin: 0 }}>Loading parties...</p>
              </div>
            ) : userParties.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
                <p style={{ margin: 0 }}>No parties found</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {userParties.map((party) => (
                  <div
                    key={party._id}
                    onClick={() => viewPartyTransactions(party)}
                    style={{ padding: '20px', background: '#f9fafb', borderRadius: '12px', border: '2px solid #e5e7eb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#eff6ff';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1a202c', marginBottom: '8px' }}>{party.name}</div>
                      <div style={{ display: 'flex', gap: '20px', fontSize: '0.85rem' }}>
                        <span style={{ color: '#667eea' }}>Purchases: <strong>{formatCurrency(party.purchases || 0)}</strong></span>
                        <span style={{ color: '#48bb78' }}>Payments: <strong>{formatCurrency(party.payments || 0)}</strong></span>
                        <span style={{ color: party.balance > 0 ? '#dc2626' : '#16a34a' }}>Balance: <strong>{formatCurrency(party.balance || 0)}</strong></span>
                      </div>
                    </div>
                    <ChevronRight size={24} color="#3b82f6" />
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={closePartiesModal} style={{ padding: '12px 32px', borderRadius: '10px', background: '#6b7280', border: 'none', color: 'white', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.background = '#4b5563'} onMouseLeave={(e) => e.target.style.background = '#6b7280'}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showTransactionsModal && selectedParty && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000, padding: '20px' }} onClick={closeTransactionsModal}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '1000px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: 700, color: '#1a202c' }}>{selectedParty.name} - Transactions</h2>
                <p style={{ margin: 0, color: '#6b7280' }}>{selectedUser.name}</p>
              </div>
              <button onClick={exportToCSV} style={{ padding: '10px 20px', borderRadius: '8px', background: '#10b981', border: 'none', color: 'white', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }} onMouseEnter={(e) => e.target.style.background = '#059669'} onMouseLeave={(e) => e.target.style.background = '#10b981'}>
                <Download size={16} />Export CSV
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #667eea' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Purchases</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a202c' }}>{formatCurrency(selectedParty.purchases || 0)}</div>
              </div>
              <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #48bb78' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Payments</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a202c' }}>{formatCurrency(selectedParty.payments || 0)}</div>
              </div>
              <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '16px', borderLeft: '4px solid #ef4444' }}>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Balance</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a202c' }}>{formatCurrency(selectedParty.balance || 0)}</div>
              </div>
            </div>

            {loadingTransactions ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #667eea', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }}></div>
                <p style={{ margin: 0 }}>Loading transactions...</p>
              </div>
            ) : partyTransactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
                <p style={{ margin: 0 }}>No transactions found</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '0.85rem' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '0.85rem' }}>Type</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '0.85rem' }}>Invoice</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '0.85rem' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partyTransactions.map((t, index) => (
                      <tr key={t._id} style={{ borderBottom: '1px solid #f3f4f6', background: index % 2 === 0 ? 'white' : '#fafbfc' }}>
                        <td style={{ padding: '12px', fontSize: '0.9rem', color: '#4b5563' }}>
                          {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600, background: `${getTypeColor(t.type)}20`, color: getTypeColor(t.type) }}>
                            {t.type}
                          </span>
                        </td>
                        <td style={{ padding: '12px', fontSize: '0.9rem', color: '#6b7280' }}>{t.invoiceNumber || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, fontSize: '0.95rem' }}>{formatCurrency(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={closeTransactionsModal} style={{ padding: '12px 32px', borderRadius: '10px', background: '#3b82f6', border: 'none', color: 'white', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.background = '#2563eb'} onMouseLeave={(e) => e.target.style.background = '#3b82f6'}>Close</button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
